// Filter rule builder (faceted plan Phase 9) — the drawer's ONLY filter
// UI. Alpine owns the rule-tree state; templ owns the markup (the
// sanctioned island: same pattern as the chips bar, NO innerHTML
// templating). The server renders nothing per keystroke: value menus
// are server JSON (field-aware, computed under the OTHER rules),
// rendered by Alpine.
//
// The tree serializes to the FLAT facet params when it is simple (one
// rule per field, flat-expressible operators — readable deep links
// preserved), else to one compact `qf` JSON param. saved_filters store
// either (no schema change). On a hand-crafted URL carrying both, qf
// wins server-side.

import { Alpine } from "./alpine";
import { authHeaders } from "./storage";

// ─── operator + field metadata (client mirror of
// internal/services/filter_registry.go — labels drive the menus) ────

// ─── operator + field vocabulary (ONE source: the Go registry) ──────
// internal/services.FilterFieldsPayload renders this page's fields,
// scope-filtered, with every operator's label/negation/arity. The
// frontend renders the wire and invents nothing (item 3.3) — the old
// hardcoded client mirror (and its known drift) is retired.
interface WireOp {
  key: string;
  label: string;
  negated?: boolean;
  arity?: OpMeta["arity"];
}

interface WireField {
  key: string;
  label: string;
  group: string;
  valueKind: FieldMeta["valueKind"];
  ops: WireOp[];
  defaultOp: string;
  suggest: boolean;
  options?: string[];
}

let wireOps: Record<string, OpMeta> = {};
let wireFieldGroups: { group: string; fields: FieldMeta[] }[] = [];
const FIELD_BY_KEY = new Map<string, FieldMeta>();

// opsForField — the op menu's item list for a field: the ingested wire
// op objects ({key, label, negated, arity}). Standalone + exported so
// the invisible-menu regression has a unit pin: the ingest hardcoded
// `ops: []` into every field (2026-10-31 user report: the op dropdown
// "did nothing" — a zero-item menu renders at zero height, no console
// error), so the op list must always round-trip from the wire.
export function opsForField(fieldKey: string): OpMeta[] {
  return FIELD_BY_KEY.get(fieldKey)?.ops ?? [];
}

export function ingestFilterFields(raw: unknown): void {
  if (!Array.isArray(raw)) return;
  const fields = raw as WireField[];
  wireOps = {};
  wireFieldGroups = [];
  FIELD_BY_KEY.clear();
  for (const f of fields) {
    if (!fieldMetaOK(f)) continue;
    const fm: FieldMeta = {
      key: f.key,
      label: f.label,
      group: f.group,
      valueKind: f.valueKind,
      // Copy the wire op objects verbatim (normalized) — opsFor() feeds
      // the op menu's x-for directly; an empty array here renders an
      // invisible zero-item menu (the 2026-10-31 "op dropdown does
      // nothing" bug).
      ops: (f.ops ?? []).map((o) => ({
        key: o.key,
        label: o.label,
        negated: o.negated === true,
        arity: (o.arity ?? "many") as OpMeta["arity"],
      })),
      defaultOp: f.defaultOp,
      suggest: f.suggest === true,
      options: f.options,
    };
    let group = wireFieldGroups.find((g) => g.group === f.group);
    if (!group) {
      group = { group: f.group, fields: [] };
      wireFieldGroups.push(group);
    }
    group.fields.push(fm);
    FIELD_BY_KEY.set(f.key, fm);
    for (const o of f.ops ?? []) {
      wireOps[o.key] = {
        key: o.key,
        label: o.label,
        negated: o.negated === true,
        arity: (o.arity ?? "many") as OpMeta["arity"],
      };
    }
  }
}

function fieldMetaOK(f: WireField): boolean {
  return (
    typeof f.key === "string" &&
    f.key !== "" &&
    typeof f.label === "string" &&
    typeof f.group === "string" &&
    typeof f.valueKind === "string" &&
    Array.isArray(f.ops) &&
    typeof f.defaultOp === "string"
  );
}

interface OpMeta {
  key: string;
  label: string;
  negated: boolean;
  arity: "none" | "one" | "two" | "many";
}

interface FieldMeta {
  key: string;
  label: string;
  group: string;
  // value kind for the cell: text (with server suggestions when
  // suggest), number, days, id (server suggestions, id values), fixed
  // (static option list), none.
  valueKind: "text" | "number" | "days" | "id" | "fixed" | "none";
  suggest: boolean;
  options?: string[];
  // The field's supported operators as the wire op objects
  // ({key, label, negated, arity}) — the op menu renders straight from
  // this list. WAS typed string[] and hardcoded [] at ingest, which
  // made every op menu render zero items (invisible menu, no console
  // error — user report 2026-10-31).
  ops: OpMeta[];
  defaultOp: string;
}

// Flat-expressible fields: key → the flat wire param a `contains`/`is`
// rule serializes to (the PINNED deep-link contract — semantics
// byte-identical to today's params).
const FLAT_PARAMS: Record<string, string> = {
  author: "author_filter",
  series: "series_filter",
  genre: "genre_filter",
  publisher: "publisher_filter",
  tags: "tags_filter",
  narrator: "narrator_filter",
  language: "language_filter",
  story_arc: "story_arc_filter",
  imprint: "imprint_filter",
  edition: "edition_filter",
  age_rating: "age_rating_filter",
  format_group: "format_group_filter",
  manga_type: "manga_type_filter",
  reading_direction: "reading_direction_filter",
};

// ─── tree state types ────────────────────────────────────────────────

export interface FilterRuleState {
  field: string;
  op: string;
  values: string[];
}

export interface FilterGroupState {
  mode: "all" | "any";
  children: (FilterRuleState | FilterGroupState)[];
  // Client-side marker: an OR-block built by the per-row connective
  // chips (Excel-style rows). Invisible in the UI — its children render
  // as one flat list; the server ignores the extra JSON key.
  implicit?: boolean;
}

export const isRule = (n: FilterRuleState | FilterGroupState): n is FilterRuleState => "field" in n;

// flatParamNames are the wire params the flat deep-link contract pins
// (plus the two flat extras the builder owns).
const FLAT_WIRE_NAMES = [
  ...Object.values(FLAT_PARAMS),
  "explicit_filter",
  "abridged_filter",
  "year_min",
  "year_max",
  "has_cover",
];

// opIsFlatExhaustible — every op that maps 1:1 onto a flat param.
export function ruleToFlatParam(rule: FilterRuleState): Record<string, string> | null {
  const joined = rule.values.join(",");
  if (!joined) return null;
  const flat = FLAT_PARAMS[rule.field];
  if (flat && rule.op === "contains") return { [flat]: joined };
  if (
    flat &&
    rule.op === "is" &&
    ["age_rating", "format_group", "manga_type", "reading_direction"].includes(rule.field)
  ) {
    return { [flat]: joined };
  }
  if (flat && rule.op === "is" && ["story_arc", "imprint", "edition"].includes(rule.field)) {
    return { [flat]: joined };
  }
  if (rule.field === "cover") {
    if (rule.op === "has") return { has_cover: "true" };
    if (rule.op === "missing") return { has_cover: "false" };
  }
  if (rule.field === "explicit" && rule.op === "is_true") return { explicit_filter: "true" };
  if (rule.field === "explicit" && rule.op === "is_false") return { explicit_filter: "false" };
  if (rule.field === "abridged" && rule.op === "is_true") return { abridged_filter: "true" };
  if (rule.field === "abridged" && rule.op === "is_false") return { abridged_filter: "false" };
  if (rule.field === "published_year" && rule.op === "at_least") return { year_min: joined };
  if (rule.field === "published_year" && rule.op === "at_most") return { year_max: joined };
  if (rule.field === "published_year" && rule.op === "between") {
    return { year_min: rule.values[0], year_max: rule.values[1] };
  }
  return null;
}

// isFlatable — the smart-serialization rule: a single ALL group of
// rules, one rule per field, every rule flat-expressible.
export function isFlatable(tree: FilterGroupState): boolean {
  if (tree.mode !== "all") return false;
  const seen = new Set<string>();
  for (const child of tree.children) {
    if (!isRule(child)) return false;
    if (seen.has(child.field)) return false;
    seen.add(child.field);
    if (!ruleToFlatParam(child)) return false;
  }
  return true;
}

// pruneIncomplete deep-clones the tree keeping only COMPLETE rules (an
// operator that takes values must have trimmed, non-empty ones), drops
// groups left empty, and can exclude the rule being edited (suggestion
// symmetry). Returns null when nothing complete remains — callers then
// send NO tree at all (an empty group is a server-side 400).
export function pruneIncomplete(
  group: FilterGroupState,
  excludePath: number[] | null,
  path: number[] = [],
): FilterGroupState | null {
  const children: (FilterRuleState | FilterGroupState)[] = [];
  group.children.forEach((child, idx) => {
    const childPath = [...path, idx];
    if (excludePath && childPath.join("-") === excludePath.join("-")) return;
    if (!isRule(child)) {
      const pruned = pruneIncomplete(child, excludePath, childPath);
      if (pruned && pruned.children.length > 0) children.push(pruned);
      return;
    }
    const arity = wireOps[child.op]?.arity ?? "many";
    const vals = (child.values ?? []).map((v) => (v ?? "").trim());
    if (arity === "none") {
      children.push({ field: child.field, op: child.op, values: [] });
      return;
    }
    const kept = vals.filter((v) => v !== "");
    if (kept.length === 0) return;
    if (arity === "two") {
      if (kept.length < 2) return;
      children.push({ field: child.field, op: child.op, values: vals });
      return;
    }
    children.push({ field: child.field, op: child.op, values: [kept.join(",")] });
  });
  if (children.length === 0) return null;
  const out: FilterGroupState = { mode: group.mode, children };
  if (group.implicit) out.implicit = true;
  return out;
}

// completeRuleValues — the completeness test from pruneIncomplete: a
// value-taking rule is a filter only with trimmed, non-empty values
// (two-arity needs both). Null = not a filter yet.
function completeRuleValues(rule: FilterRuleState): string[] | null {
  const arity = wireOps[rule.op]?.arity ?? "many";
  if (arity === "none") return [];
  const vals = (rule.values ?? []).map((v) => (v ?? "").trim());
  const kept = vals.filter((v) => v !== "");
  if (kept.length === 0) return null;
  if (arity === "two" && kept.length < 2) return null;
  return arity === "two" ? vals : [kept.join(",")];
}

// negateRule — a same-field OR sibling's negated twin (contains ↔
// not_contains, is ↔ is_not, in ↔ not_in): the sibling's picked values
// come OFF the menu while every other value stays ("we've selected
// Lewis Carroll — remove him, show the rest of the ebook authors").
// An ALREADY-NEGATED sibling picks nothing, so it drops instead of
// flipping positive (a positive flip would narrow, not widen). Null =
// no twin (or nothing picked): the sibling just drops.
export function negateRule(rule: FilterRuleState): FilterRuleState | null {
  if (rule.op.startsWith("not_")) return null;
  const twin = opsForField(rule.field).find((o) => o.key === `not_${rule.op}`);
  if (!twin) return null;
  const vals = completeRuleValues(rule);
  if (vals === null) return null;
  return { field: rule.field, op: twin.key, values: vals };
}

// suggestTree — the constraint tree for one rule's value menu. The
// edited rule's own constraint is gone; walking root → edited rule:
// • ALL-group siblings stay (AND narrows candidates: "library =
//   ebooks" keeps the menu to ebook authors);
// • ANY-group siblings are independent alternatives — they drop,
//   EXCEPT a same-field sibling, whose picked values NEGATE into the
//   chain (2026-10-31 user spec: OR must keep the ebook scope, take
//   the already-picked author off the list, and offer the rest);
// • incomplete rules never constrain (they are not filters yet).
// The kept constraints are conjunctive, so the result is one ALL
// chain; null when nothing constrains (the caller sends no qf).
export function suggestTree(
  group: FilterGroupState,
  path: number[],
  fieldKey: string,
): FilterGroupState | null {
  if (path.length === 0) return null;
  const onPath = group.children[path[0]];
  if (!onPath) return null;
  const children: (FilterRuleState | FilterGroupState)[] = [];
  group.children.forEach((child, idx) => {
    if (idx === path[0]) return;
    if (group.mode === "any") {
      if (isRule(child) && child.field === fieldKey) {
        const neg = negateRule(child);
        if (neg) children.push(neg);
      }
      return; // OR siblings never narrow the menu
    }
    if (isRule(child)) {
      const vals = completeRuleValues(child);
      if (vals !== null) children.push({ field: child.field, op: child.op, values: vals });
      return;
    }
    const pruned = pruneIncomplete(child, null);
    if (pruned && pruned.children.length > 0) children.push(pruned);
  });
  if (path.length > 1 && !isRule(onPath)) {
    const nested = suggestTree(onPath, path.slice(1), fieldKey);
    if (nested && nested.children.length > 0) children.push(nested);
  }
  if (children.length === 0) return null;
  return { mode: "all", children };
}

// ─── row projection (flat rendering of the nested tree) ─────────────

interface BuilderRow {
  key: string;
  path: number[];
  type: "rule" | "groupOpen" | "groupClose";
  depth: number;
  // How this row joins the row above: AND | OR. Clickable on rules
  // where toggling restructures the tree (Excel-style per-row
  // connective); static on group boundaries and first rows.
  chip: "AND" | "OR";
  chipClickable: boolean;
  isFirst: boolean;
  rule?: FilterRuleState;
  group?: FilterGroupState;
}

function projectRows(
  group: FilterGroupState,
  path: number[],
  depth: number,
  out: BuilderRow[],
): void {
  group.children.forEach((child, idx) => {
    const childPath = [...path, idx];
    const key = childPath.join("-");
    // The slot chip: how this child joins the row above it.
    const slotChip: "AND" | "OR" = idx === 0 || group.mode === "all" ? "AND" : "OR";
    if (!isRule(child)) {
      const g = child as FilterGroupState;
      if (g.implicit) {
        // Invisible OR-block: its children continue the flat list —
        // the first inherits this slot's chip, the rest are ORs.
        g.children.forEach((sub, j) => {
          const subPath = [...childPath, j];
          const subKey = subPath.join("-");
          if (!isRule(sub)) {
            return; // implicit blocks hold rules only
          }
          out.push({
            key: subKey,
            path: subPath,
            type: "rule",
            depth,
            chip: j === 0 ? slotChip : "OR",
            chipClickable: j > 0,
            isFirst: idx === 0 && j === 0,
            rule: sub,
          });
        });
        return;
      }
      out.push({
        key,
        path: childPath,
        type: "groupOpen",
        depth,
        chip: slotChip,
        chipClickable: false,
        isFirst: idx === 0,
        group: g,
      });
      projectRows(g, childPath, depth + 1, out);
      out.push({
        key: key + "-close",
        path: childPath,
        type: "groupClose",
        depth,
        chip: slotChip,
        chipClickable: false,
        isFirst: idx === 0,
        group: g,
      });
      return;
    }
    const prev = group.children[idx - 1];
    const clickable = idx > 0 && !!prev && (isRule(prev) || !!(prev as FilterGroupState).implicit);
    out.push({
      key,
      path: childPath,
      type: "rule",
      depth,
      chip: slotChip,
      chipClickable: clickable,
      isFirst: idx === 0,
      rule: child,
    });
  });
}

// ─── the Alpine component slice ──────────────────────────────────────

interface Suggestion {
  value: string;
  count: number;
  display?: string;
}

export function filterBuilderData() {
  return {
    // Lifecycle: the Go registry renders this page's vocabulary (the
    // data-filter-fields payload); ingest once so the menus are born
    // scope-filtered and server-owned (item 3.3).
    scopeKey: "books" as string,
    init() {
      const el = this.$el as HTMLElement | null;
      const raw = el?.dataset.filterFields;
      if (raw && !FIELD_BY_KEY.size) {
        try {
          ingestFilterFields(JSON.parse(raw));
        } catch {
          // A malformed payload leaves empty menus — the server is
          // source-of-truth; never invent rows client-side.
        }
      }
      // SSR-embedded id labels (2026-11-01): the page handler resolved
      // the tree's id-kind values to names at render time and embedded
      // the map here — ingest it BEFORE any tree lands, so the cells
      // show names from the very first paint and resolveIdLabels (the
      // label endpoint below) early-returns instead of hitting it.
      const labelsRaw = el?.dataset.idLabels;
      if (labelsRaw) {
        try {
          Object.assign(this.idLabels, JSON.parse(labelsRaw) as Record<string, string>);
        } catch {
          // malformed embed — cells fall back to raw ids, as before
        }
      }
      // Collection mounts (item 3.4): the page carries the collection's
      // scope (row universe for counts/apply) and its PREFILL tree (the
      // stored tree, or the legacy rules' conversion).
      if (el?.dataset.scope) {
        this.scopeKey = el.dataset.scope;
      }
      const treeRaw = el?.dataset.tree;
      if (treeRaw) {
        try {
          const t = JSON.parse(treeRaw) as FilterGroupState;
          if (t && Array.isArray(t.children)) {
            this.filterTree = { mode: t.mode === "any" ? "any" : "all", children: t.children };
          }
        } catch {
          // no prefill — start empty
        }
      } else {
        // Bookshelf mounts carry NO data-tree: the wire state lives in
        // the form (applyURLFilters put the URL's qf there). It must
        // hydrate HERE, at this component's init — the parent's x-init
        // (initBookshelf) runs BEFORE this nested component exists
        // (Alpine initializes parents first), so its builderData()
        // call is null and its hydrateFromForm never lands (2026-11-01:
        // a refresh left the drawer empty — no rules at all).
        this.hydrateFromForm();
      }
      // Anything still unlabeled (a tree hydrated from a form the SSR
      // didn't know about) resolves through the label endpoint.
      this.resolveIdLabels();
    },
    // treeJSON is the APPLY wire for collection mounts: the page's
    // scope rides the tree's top-level key (the engine's scope head).
    treeJSON(): string {
      return JSON.stringify({
        scope: this.scopeKey,
        mode: this.filterTree.mode,
        children: this.filterTree.children,
      });
    },
    // applyCollectionTree delegates to the page-registered hook (the
    // collection editor PUTs the tree; All-Books never sets one).
    applyCollectionTree(): void {
      const store = Alpine.store("collectionTreeApply") as
        | { hook?: (wire: string) => void }
        | undefined;
      if (store?.hook) store.hook(this.treeJSON());
    },
    // Rule tree (source of truth; hidden wire inputs are synced after
    // every mutation).
    filterTree: { mode: "all", children: [] } as FilterGroupState,
    matchCount: null as number | null,
    matchCountLoading: false,
    openMenu: "" as string, // "<rowKey>:field" | "<rowKey>:op" | "<rowKey>:value" | ""
    fieldFilter: "" as string, // the field menu's filter input
    suggestions: {} as Record<string, Suggestion[]>,
    suggestionsOpen: "" as string, // rowKey whose value menu is open
    suggestionsLoading: "" as string, // rowKey currently fetching
    idLabels: {} as Record<string, string>, // id → display, from suggestion fetches
    idDrafts: {} as Record<string, string>, // id-kind rowKey → the NAME text being typed (display draft; stored values stay ids)
    _suggestTimer: null as number | null,
    _countTimer: null as number | null,

    get fieldGroups() {
      return wireFieldGroups;
    },

    get builderRows(): BuilderRow[] {
      const rows: BuilderRow[] = [];
      // The ROOT projects as its own header row (2026-11-01): its mode
      // was invisible before — no header, no control, permanently "all".
      // path=[] distinguishes it from explicit groups (whose labels read
      // "Group"); nodeAt([]) IS the root, so the header's toggle reaches
      // it like any other group.
      rows.push({
        key: "root",
        path: [],
        type: "groupOpen",
        depth: 0,
        chip: "AND",
        chipClickable: false,
        isFirst: true,
        group: this.filterTree as FilterGroupState,
      });
      projectRows(this.filterTree as FilterGroupState, [], 0, rows);
      return rows;
    },

    // ── lookups ─────────────────────────────────────────────────────
    fieldMeta(key: string): FieldMeta | undefined {
      return FIELD_BY_KEY.get(key);
    },
    // CSP-safe accessor: attribute expressions cannot use optional
    // chaining (the CSP parser rejects `?.`), so the null-walk lives
    // here.
    fieldIsDays(key: string): boolean {
      return this.fieldMeta(key)?.valueKind === "days";
    },
    // CSP-safe click-outside for the value menu (attribute expressions
    // cannot carry if-statements).
    dismissSuggestions(e: Event): void {
      const target = e.target as HTMLElement | null;
      if (!target || !target.closest(".fb-value-cell")) {
        this.suggestionsOpen = "";
      }
    },
    fieldLabelOf(key: string): string {
      return FIELD_BY_KEY.get(key)?.label ?? "Field";
    },
    opsFor(fieldKey: string): OpMeta[] {
      return opsForField(fieldKey);
    },
    opMeta(opKey: string): OpMeta | undefined {
      return wireOps[opKey];
    },
    opLabelOf(rule: FilterRuleState): string {
      const m = wireOps[rule.op];
      return m ? m.label : rule.op;
    },
    isNegated(rule: FilterRuleState): boolean {
      return wireOps[rule.op]?.negated ?? false;
    },
    valueArity(rule: FilterRuleState): OpMeta["arity"] {
      return wireOps[rule.op]?.arity ?? "many";
    },
    hasValueCell(rule: FilterRuleState): boolean {
      const field = FIELD_BY_KEY.get(rule.field);
      if (!field) return false;
      return field.valueKind !== "none" && this.valueArity(rule) !== "none";
    },

    // ── mutations (each ends in form sync + live count refresh) ────
    _touch(): void {
      this.syncFormInputs();
      this.scheduleMatchCount();
    },
    ensureReadyRule(): void {
      if (this.filterTree.children.length === 0) {
        const first = wireFieldGroups[0]?.fields[0]?.key ?? "author";
        const fm = FIELD_BY_KEY.get(first);
        this.filterTree.children.push({
          field: first,
          op: fm?.defaultOp ?? "contains",
          values: [],
        });
        this._touch();
      }
    },
    nodeAt(path: number[]): FilterRuleState | FilterGroupState | null {
      let node: FilterRuleState | FilterGroupState = this.filterTree;
      for (const idx of path) {
        if (!("children" in node)) return null;
        const next = (node as FilterGroupState).children[idx];
        if (!next) return null;
        node = next;
      }
      return node;
    },
    // groupMode — a group's current all/any (root included: path=[] IS
    // the root via nodeAt). Anything malformed reads as "all".
    groupMode(path: number[]): "all" | "any" {
      const node = this.nodeAt(path);
      if (!node || isRule(node)) return "all";
      return node.mode === "any" ? "any" : "all";
    },

    // toggleGroupMode — the group header's flip: ALL ⇄ ANY for that
    // group only. The engine has always honored group modes
    // (buildGroupClause parenthesizes nested groups), so this is pure
    // UI exposure; the row-level chips and implicit OR-blocks are
    // untouched (they mix AND/OR inside a group — a different, orthogonal
    // feature).
    toggleGroupMode(path: number[]): void {
      const node = this.nodeAt(path);
      if (!node || isRule(node)) return;
      node.mode = node.mode === "any" ? "all" : "any";
      this._touch();
    },

    addRule(path: number[]): void {
      const parent = this.nodeAt(path);
      if (!parent || !("children" in parent)) return;
      (parent as FilterGroupState).children.push({ field: "author", op: "contains", values: [] });
      this._touch();
    },
    addGroup(path: number[]): void {
      const parent = this.nodeAt(path);
      if (!parent || !("children" in parent)) return;
      (parent as FilterGroupState).children.push({
        mode: "all",
        children: [{ field: "author", op: "contains", values: [] }],
      });
      this._touch();
    },
    removeNode(path: number[]): void {
      if (path.length === 0) return;
      const parentPath = path.slice(0, -1);
      const idx = path[path.length - 1];
      const parent = this.nodeAt(parentPath);
      if (!parent || !("children" in parent)) return;
      (parent as FilterGroupState).children.splice(idx, 1);
      if (this.openMenu.startsWith(path.join("-") + ":")) this.openMenu = "";
      this._touch();
    },
    // toggleRuleConnective — Excel-style per-row AND/OR. Clicking the
    // chip before a rule flips how THAT rule joins the row above:
    // AND → OR moves it into an implicit OR-block with the previous
    // rule(s); OR → AND pulls it back out to the top level. The tree
    // stays the single source of truth; nothing else changes.
    toggleRuleConnective(rulePath: number[]): void {
      const parentPath = rulePath.slice(0, -1);
      const parent = this.nodeAt(parentPath);
      if (!parent || !("children" in parent)) return;
      const list = parent as FilterGroupState;
      const idx = rulePath[rulePath.length - 1];
      const rule = list.children[idx];
      if (!rule || !isRule(rule)) return;

      if (list.implicit) {
        // OR → AND: pull the rule out of the OR-block, re-inserting it
        // right after the block at the parent level.
        const gpPath = parentPath.slice(0, -1);
        const gp = this.nodeAt(gpPath);
        if (!gp || !("children" in gp)) return;
        const gpList = gp as FilterGroupState;
        const blockIdx = parentPath[parentPath.length - 1];
        list.children.splice(idx, 1);
        gpList.children.splice(blockIdx + 1, 0, rule);
        if (list.children.length === 1) {
          gpList.children.splice(blockIdx, 1, list.children[0]);
        } else if (list.children.length === 0) {
          gpList.children.splice(blockIdx, 1);
        }
      } else {
        // AND → OR: join the previous sibling rule / OR-block.
        if (idx === 0) return;
        const prev = list.children[idx - 1];
        if (!prev) return;
        if (isRule(prev)) {
          const block: FilterGroupState = {
            mode: "any",
            implicit: true,
            children: [prev, rule],
          };
          list.children.splice(idx - 1, 2, block);
        } else if ((prev as FilterGroupState).implicit) {
          (prev as FilterGroupState).children.push(rule);
          list.children.splice(idx, 1);
        } else {
          return; // previous is an explicit group: no row-level toggle
        }
      }
      this._touch();
    },
    setRuleField(path: number[], key: string): void {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return;
      if (node.field === key) {
        this.openMenu = "";
        return;
      }
      // Field switch clears the value + resets the operator to the
      // kind's default (approved behavior).
      node.field = key;
      const meta = FIELD_BY_KEY.get(key);
      node.op = meta?.defaultOp ?? "contains";
      node.values = [];
      this.openMenu = "";
      delete this.suggestions[path.join("-")];
      this._touch();
    },
    setRuleOp(path: number[], opKey: string): void {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return;
      node.op = opKey;
      if (this.valueArity(node) === "none") {
        node.values = [];
      } else if (this.valueArity(node) === "two") {
        while (node.values.length < 2) node.values.push("");
      } else if (node.values.length > 1) {
        // keep the typed values; multi stays valid for many-arity
      } else if (node.values.length === 0) {
        node.values = [""];
      }
      this.openMenu = "";
      this._touch();
    },
    // resolveIdLabels — fire-and-forget wrapper (init/ingest paths).
    resolveIdLabels(): void {
      void this.resolveLabelsAsync();
    },

    // resolveLabelsAsync — names every UNLABELED id-kind value in the
    // tree through the label endpoint (entity tables, no media
    // constraints). The SSR embed covers page renders; this covers
    // trees the client holds that the server never saw — applying a
    // SAVED filter from a cold bookshelf (2026-11-01: the old fallback
    // reused the suggest facet, which labels only the ids whose media
    // matches the tree's sibling rules). Resolves when labels have
    // landed so callers can refresh id-labeled UI (the chips bar).
    async resolveLabelsAsync(): Promise<void> {
      const byField: Record<string, Set<string>> = {};
      const visit = (node: FilterRuleState | FilterGroupState): void => {
        if (isRule(node)) {
          if (FIELD_BY_KEY.get(node.field)?.valueKind !== "id") return;
          const ids = node.values
            .join(",")
            .split(",")
            .filter((v) => v.trim() !== "");
          if (ids.length === 0) return;
          const unlabeled = ids.filter((v) => this.idLabels[v] === undefined);
          if (unlabeled.length === 0) return;
          const set = (byField[node.field] ??= new Set<string>());
          unlabeled.forEach((v) => set.add(v));
          return;
        }
        node.children.forEach(visit);
      };
      this.filterTree.children.forEach(visit);
      const fields = Object.keys(byField);
      if (fields.length === 0) return;
      await Promise.all(
        fields.map(async (field) => {
          const ids = [...byField[field]].join(",");
          try {
            const response = await fetch(
              `/api/filter/labels?field=${encodeURIComponent(field)}&ids=${encodeURIComponent(ids)}`,
              { headers: authHeaders() },
            );
            if (!response.ok) return;
            const data = (await response.json()) as { labels?: Record<string, string> };
            for (const [id, label] of Object.entries(data.labels ?? {})) {
              this.idLabels[id] = label;
            }
          } catch {
            // offline/failed: cells keep the raw ids (the pre-embed floor)
          }
        }),
      );
    },

    // valuePlaceholderFor — the value cell's hint. Id-kind cells are
    // the MULTI-VALUE boxes (pick several; the menu keeps toggling
    // them in) — every operator on an id field accepts a set, so the
    // hint is uniform (2026-11-01, Option A: one name per field, no
    // per-operator distinction — "is" and "in" were synonyms).
    valuePlaceholderFor(rule: FilterRuleState): string {
      if (this.fieldIsDays(rule.field)) return "days";
      if (FIELD_BY_KEY.get(rule.field)?.valueKind === "id") return "pick one or more";
      return "value";
    },

    // isIdValueRow — the value cell stores IDS (library/collection/
    // feed/added_by): display needs the label map, typing filters NAMES.
    isIdValueRow(path: number[]): boolean {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return false;
      return FIELD_BY_KEY.get(node.field)?.valueKind === "id";
    },

    // idChipsFor — the chipbox's chips: the rule's joined values split
    // and labeled through idLabels (raw id fallback while resolution is
    // in flight or impossible).
    idChipsFor(path: number[]): { id: string; label: string }[] {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return [];
      return node.values
        .join(",")
        .split(",")
        .filter(Boolean)
        .map((v) => ({ id: v, label: this.idLabels[v] ?? v }));
    },

    // idDraftFor — the chipbox's inner input shows ONLY the typing
    // draft; labels live on the chips.
    idDraftFor(path: number[]): string {
      return this.idDrafts[path.join("-")] ?? "";
    },

    // removeIdChip — drop one picked id from the rule's joined values
    // (order preserved) and refresh the match count. The suggestion
    // menu never offers a picked value; the chip × is the faster
    // affordance.
    removeIdChip(path: number[], id: string): void {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return;
      const rest = node.values
        .join(",")
        .split(",")
        .filter((v) => v && v !== id);
      node.values = rest.length ? [rest.join(",")] : [];
      this._touch();
    },

    // chipInputKeydown — Backspace on an EMPTY chip input removes the
    // last chip (standard chip-box affordance); any typing keeps the
    // normal draft flow.
    chipInputKeydown(path: number[], event: KeyboardEvent): void {
      if (event.key !== "Backspace") return;
      const el = event.target as HTMLInputElement | null;
      if (el && el.value !== "") return;
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return;
      const ids = node.values.join(",").split(",").filter(Boolean);
      if (ids.length === 0) return;
      ids.pop();
      node.values = ids.length ? [ids.join(",")] : [];
      this._touch();
    },

    // valueCellText — what a NON-id value input displays (text/number
    // cells write straight through). Id-kind cells render chips
    // (idChipsFor) — their input carries only the typing draft.
    valueCellText(path: number[]): string {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return "";
      return node.values.join(",");
    },

    // onValueCellInput — id-kind cells type a NAME filter (the draft);
    // the stored values stay ids, updated only by picking from the
    // menu. Every other kind writes straight through to the rule.
    onValueCellInput(path: number[], value: string): void {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return;
      if (this.isIdValueRow(path)) {
        const key = path.join("-");
        if (value === "") delete this.idDrafts[key];
        else this.idDrafts[key] = value;
        this.scheduleSuggestions(key, path, node);
        this._touch();
        return;
      }
      this.setRuleValue(path, 0, value);
    },

    setRuleValue(path: number[], idx: number, value: string): void {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return;
      node.values[idx] = value;
      this._touch();
      if (idx === 0 && FIELD_BY_KEY.get(node.field)?.suggest) {
        this.scheduleSuggestions(path.join("-"), path, node);
      }
    },
    pickSuggestion(_rowKey: string, path: number[], s: Suggestion): void {
      const node = this.nodeAt(path);
      if (!node || !isRule(node)) return;
      const meta = FIELD_BY_KEY.get(node.field);
      this.idLabels[s.value] = s.display ?? s.value;
      delete this.idDrafts[path.join("-")]; // the cell shows labels again
      if (meta?.valueKind === "id") {
        // id-valued MULTI: toggle the id in the comma list.
        const ids = node.values.join(",").split(",").filter(Boolean);
        const at = ids.indexOf(s.value);
        if (at >= 0) {
          ids.splice(at, 1);
        } else {
          ids.push(s.value);
        }
        node.values = [ids.join(",")];
      } else if (this.valueArity(node) === "two") {
        node.values[0] = s.value;
      } else {
        node.values = [s.value];
      }
      this.suggestionsOpen = "";
      this._touch();
    },
    clearBuilder(): void {
      this.filterTree = { mode: "all", children: [] };
      this.matchCount = null;
      this.suggestions = {};
      this.suggestionsOpen = "";
      this.openMenu = "";
      this._touch();
    },

    // ── menus ───────────────────────────────────────────────────────
    toggleMenu(menuKey: string): void {
      this.openMenu = this.openMenu === menuKey ? "" : menuKey;
      if (this.openMenu.endsWith(":field")) {
        this.fieldFilter = "";
      }
      if (this.openMenu.endsWith(":value")) {
        this.suggestionsOpen = this.openMenu.split(":")[0];
      }
    },

    // toggleConnective — CSP-safe wrapper for the connective chip: the
    // CSP build's evaluator rejects `if` STATEMENTS in attribute
    // expressions, so the chip's `@click="if (row.chipClickable)
    // toggleRuleConnective(row.path)"` silently never ran (user report
    // 2026-10-31). The clickability check lives here now.
    toggleConnective(row: BuilderRow): void {
      if (!row.chipClickable) return;
      this.toggleRuleConnective(row.path);
    },

    // visibleFieldGroups — the field menu's groups, filtered by the
    // menu's filter input (label or key substring, case-insensitive);
    // groups with no matches hide.
    get visibleFieldGroups(): { group: string; fields: FieldMeta[] }[] {
      const q = this.fieldFilter.trim().toLowerCase();
      if (!q) return wireFieldGroups;
      return wireFieldGroups
        .map((g) => ({
          group: g.group,
          fields: g.fields.filter(
            (f) => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q),
          ),
        }))
        .filter((g) => g.fields.length > 0);
    },

    // ── suggestions (field-aware, under the OTHER rules) ────────────
    scheduleSuggestions(rowKey: string, path: number[], rule: FilterRuleState): void {
      if (this._suggestTimer !== null) window.clearTimeout(this._suggestTimer);
      this._suggestTimer = window.setTimeout(() => {
        void this.fetchSuggestions(rowKey, path, rule);
      }, 250);
    },
    async fetchSuggestions(rowKey: string, path: number[], rule: FilterRuleState): Promise<void> {
      const meta = FIELD_BY_KEY.get(rule.field);
      if (!meta || !meta.suggest) return;
      // Fixed-option fields never hit the server.
      if (meta.valueKind === "fixed") return;
      const typed = (
        this.isIdValueRow(path) ? (this.idDrafts[path.join("-")] ?? "") : (rule.values[0] ?? "")
      ).trim();
      // 1 char is too little to filter on; EMPTY means BROWSE — the
      // user clicked the box to see the field's top values under the
      // other rules (the server answers the blank query with the
      // un-typed top list).
      if (typed.length === 1) return;
      // cookie auth carries the call; a null bearer must not strand it
      // Open the menu NOW (loading state) — never a blank flash.
      this.suggestions[rowKey] = [];
      this.suggestionsLoading = rowKey;
      this.suggestionsOpen = rowKey;
      // The menu is computed under the suggest-constraint tree: AND
      // siblings keep narrowing, OR siblings drop (a same-field pick
      // negates in, taking its value off the list), and the edited
      // rule itself never constrains. Null = nothing constrains: no
      // qf rides (an empty group is a server-side 400).
      const others = suggestTree(this.filterTree, path, rule.field);
      const params = new URLSearchParams();
      params.set(rule.field, typed || " ");
      if (others) params.set("qf", JSON.stringify(others));
      const currentLibraryId = (document.getElementById("library-select") as HTMLSelectElement)
        ?.value;
      if (currentLibraryId) params.set("library_id", currentLibraryId);
      // Browse (no typing) shows a slightly wider window than filter-
      // as-you-type.
      params.set("limit", typed ? "8" : "15");
      try {
        const response = await fetch(`/api/media-items/search?${params.toString()}`, {
          headers: authHeaders(),
        });
        if (!response.ok) {
          console.warn(`suggestion fetch failed (${response.status})`);
          this.suggestions[rowKey] = [];
          return;
        }
        const data = (await response.json()) as { results?: Suggestion[] };
        for (const s of data.results ?? []) {
          if (s.display) this.idLabels[s.value] = s.display;
        }
        this.suggestions[rowKey] = data.results ?? [];
      } catch (error) {
        console.warn("suggestion fetch failed", error);
        this.suggestions[rowKey] = [];
      } finally {
        if (this.suggestionsLoading === rowKey) this.suggestionsLoading = "";
      }
    },
    openValueMenu(rowKey: string, path: number[], rule: FilterRuleState): void {
      const meta = FIELD_BY_KEY.get(rule.field);
      if (!meta) return;
      if (meta.valueKind === "fixed") {
        // Static options: no fetch.
        this.suggestions[rowKey] = (meta.options ?? []).map((v) => ({
          value: v,
          count: 0,
        }));
        this.suggestionsOpen = rowKey;
        return;
      }
      if (meta.suggest) {
        void this.fetchSuggestions(rowKey, path, rule);
        return;
      }
      // Plain free-input field: no value menu.
    },
    suggestionShown(rowKey: string): boolean {
      // Open for the whole life of the interaction — an empty result
      // renders the empty state, it never flashes the menu closed.
      return this.suggestionsOpen === rowKey;
    },

    // ── live count ──────────────────────────────────────────────────
    scheduleMatchCount(): void {
      if (this._countTimer !== null) window.clearTimeout(this._countTimer);
      this._countTimer = window.setTimeout(() => {
        void this.fetchMatchCount();
      }, 350);
    },
    async fetchMatchCount(): Promise<void> {
      // cookie auth carries the call; a null bearer must not strand it
      // Incomplete rules are NOT filters yet — the count runs on the
      // completed tree only (an incomplete rule would be a 400).
      const completed = this.completedTree(null);
      if (!completed) {
        this.matchCount = null;
        return;
      }
      const params = new URLSearchParams();
      const scoped = this.scopedTree();
      if (scoped) params.set("qf", JSON.stringify(scoped));
      const currentLibraryId = (document.getElementById("library-select") as HTMLSelectElement)
        ?.value;
      if (currentLibraryId) params.set("library_id", currentLibraryId);
      this.matchCountLoading = true;
      try {
        const response = await fetch(`/api/media-items/filter-count?${params.toString()}`, {
          headers: authHeaders(),
        });
        if (!response.ok) {
          this.matchCount = null;
          return;
        }
        const data = (await response.json()) as { count?: number };
        this.matchCount = data.count ?? null;
      } catch {
        this.matchCount = null;
      } finally {
        this.matchCountLoading = false;
      }
    },

    // ── serialization + form sync ───────────────────────────────────
    // treeWithout returns the tree with the node at `path` removed (the
    // "other rules" for a rule's own value menu).
    treeWithout(path: number[]): FilterGroupState | null {
      const clone: FilterGroupState = JSON.parse(JSON.stringify(this.filterTree));
      if (path.length === 0) return clone;
      const parentPath = path.slice(0, -1);
      let node: FilterGroupState = clone;
      for (const idx of parentPath) {
        const next = node.children[idx];
        if (!next || isRule(next)) return clone;
        node = next;
      }
      node.children.splice(path[path.length - 1], 1);
      return clone;
    },
    serializeFilters():
      | { mode: "flat"; params: Record<string, string> }
      | { mode: "qf"; qf: string } {
      // The COMPLETED tree is what the wire sees — an incomplete rule
      // was never a filter, so applying must not send it (the server
      // would 400 and the grid would break).
      const completed = this.completedTree(null);
      if (!completed) {
        return { mode: "flat", params: {} };
      }
      if (isFlatable(completed)) {
        const params: Record<string, string> = {};
        for (const child of completed.children) {
          if (!isRule(child)) continue;
          const rule = child as FilterRuleState;
          Object.assign(params, ruleToFlatParam(rule));
        }
        return { mode: "flat", params };
      }
      return { mode: "qf", qf: JSON.stringify(completed) };
    },

    // completedTree — the pruned clone (see pruneIncomplete).
    completedTree(excludePath: number[] | null): FilterGroupState | null {
      return pruneIncomplete(this.filterTree as FilterGroupState, excludePath);
    },
    // scopedTree is the WIRE tree: the mount's scope rides the top
    // level (the engine's scope head — counts and applies carry it).
    scopedTree(): Record<string, unknown> | null {
      const completed = this.completedTree(null);
      if (!completed) return null;
      return { scope: this.scopeKey, mode: completed.mode, children: completed.children };
    },
    syncFormInputs(): void {
      const form = document.getElementById("filter-form") as HTMLFormElement | null;
      if (!form) return;
      // Drop every previously managed wire input, then write the
      // current serialization.
      for (const name of [...FLAT_WIRE_NAMES, "qf"]) {
        form.querySelectorAll(`input[name="${name}"]`).forEach((el) => el.remove());
      }
      const s = this.serializeFilters();
      const add = (name: string, value: string) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };
      if (s.mode === "qf") {
        add("qf", s.qf);
        return;
      }
      const flatParams = s.params as Record<string, string>;
      for (const [name, value] of Object.entries(flatParams)) {
        add(name, value);
      }
    },

    // ── hydration (URL deep links + saved filters) ──────────────────
    // hydrateFromForm reads the form's wire inputs into the tree: a qf
    // input wins (pinned precedence), else the flat params become one
    // rule each. Returns true when anything was applied.
    hydrateFromForm(): boolean {
      const form = document.getElementById("filter-form") as HTMLFormElement | null;
      if (!form) return false;
      const qfInput = form.querySelector('input[name="qf"]') as HTMLInputElement | null;
      if (qfInput && qfInput.value) {
        try {
          const tree = JSON.parse(qfInput.value) as FilterGroupState;
          if (tree && Array.isArray(tree.children)) {
            this.filterTree = tree;
            // Hydrated trees carry bare ids with no labels in sight
            // (page refresh with an active saved filter) — resolve
            // them now (2026-11-01: the cells showed raw uuids).
            this.resolveIdLabels();
            return true;
          }
        } catch {
          // malformed qf: fall through to flat mapping
        }
      }
      const rules: FilterRuleState[] = [];
      for (const [fieldKey, wire] of Object.entries(FLAT_PARAMS)) {
        const input = form.querySelector(`input[name="${wire}"]`) as HTMLInputElement | null;
        if (!input || !input.value) continue;
        const meta = FIELD_BY_KEY.get(fieldKey);
        const op = meta && meta.ops.some((o) => o.key === "contains") ? "contains" : "is";
        rules.push({ field: fieldKey, op, values: [input.value] });
      }
      const cover = form.querySelector('input[name="has_cover"]') as HTMLInputElement | null;
      if (cover && cover.value) {
        rules.push({ field: "cover", op: cover.value === "true" ? "has" : "missing", values: [] });
      }
      const explicitIn = form.querySelector(
        'input[name="explicit_filter"]',
      ) as HTMLInputElement | null;
      if (explicitIn && explicitIn.value) {
        rules.push({
          field: "explicit",
          op: explicitIn.value === "true" ? "is_true" : "is_false",
          values: [],
        });
      }
      const abridgedIn = form.querySelector(
        'input[name="abridged_filter"]',
      ) as HTMLInputElement | null;
      if (abridgedIn && abridgedIn.value) {
        rules.push({
          field: "abridged",
          op: abridgedIn.value === "true" ? "is_true" : "is_false",
          values: [],
        });
      }
      const yearMin = form.querySelector('input[name="year_min"]') as HTMLInputElement | null;
      if (yearMin && yearMin.value) {
        rules.push({ field: "published_year", op: "at_least", values: [yearMin.value] });
      }
      const yearMax = form.querySelector('input[name="year_max"]') as HTMLInputElement | null;
      if (yearMax && yearMax.value) {
        rules.push({ field: "published_year", op: "at_most", values: [yearMax.value] });
      }
      if (rules.length === 0) return false;
      this.filterTree = { mode: "all", children: rules };
      return true;
    },

    // chipLabelFor — one rule's chip text.
    chipLabelFor(rule: FilterRuleState): string {
      const fieldLabel = this.fieldLabelOf(rule.field);
      const opLabel = this.opLabelOf(rule);
      const meta = FIELD_BY_KEY.get(rule.field);
      let value = "";
      if (this.valueArity(rule) !== "none" && meta?.valueKind !== "none") {
        const names = rule.values
          .join(",")
          .split(",")
          .filter(Boolean)
          .map((v) => this.idLabels[v] ?? v);
        value = names.length > 0 ? ` ${names.join(", ")}` : "";
        if (meta?.valueKind === "days") value = ` ${rule.values[0]} days`;
      }
      return `${fieldLabel} ${opLabel}${value}`.trim();
    },
  };
}

// Component registration (main.ts imports this module for the side
// effect, same pattern as bookshelf.ts).
Alpine.data("filterBuilderExtra", filterBuilderData);
